import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import { api } from '@/lib/api'

interface RescrapeJob {
  jobId: string
  status: 'running' | 'completed' | 'failed'
  error?: string | null
  changesApplied?: boolean
}

interface ProfileSnapshot {
  id: string
  userId: string
  contactId: string
  snapshotData: Record<string, unknown>
  source: string
  createdAt: string
}

interface ProfileSnapshotsResponse {
  items: ProfileSnapshot[]
  total: number
  page: number
}

export function useStartRescrape() {
  return useMutation({
    mutationFn: (contactId: string) =>
      api<RescrapeJob>(`/api/contacts/${contactId}/rescrape`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('LinkedIn rescrape started')
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to start rescrape')
    },
  })
}

export function useRescrapeJobStatus(contactId: string | undefined, jobId: string | undefined) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: ['rescrape-job', contactId, jobId],
    queryFn: async () => {
      const result = await api<RescrapeJob>(`/api/contacts/${contactId}/rescrape/jobs/${jobId}`)
      if (result.status === 'completed') {
        qc.invalidateQueries({ queryKey: ['contacts', contactId] })
        qc.invalidateQueries({ queryKey: ['profile-snapshots', contactId] })
        toast.success('LinkedIn rescrape complete')
      } else if (result.status === 'failed') {
        toast.error(result.error || 'Rescrape failed')
      }
      return result
    },
    enabled: !!contactId && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.status === 'completed' || data?.status === 'failed') return false
      return 3000
    },
  })
}

export function useProfileSnapshots(contactId: string | undefined, page: number = 1) {
  return useQuery({
    queryKey: ['profile-snapshots', contactId, page],
    queryFn: () =>
      api<ProfileSnapshotsResponse>(
        `/api/contacts/${contactId}/profile-snapshots?page=${page}&limit=10`,
      ),
    enabled: !!contactId,
  })
}
